import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { logger } from '@/utils/logger';
import { authApi } from '../lib/api';

const PermissionsContext = createContext();

// Cache TTL: 1 hour (in milliseconds)
const CACHE_TTL = 60 * 60 * 1000;
const CACHE_KEY = 'user_permissions';
const CACHE_TIMESTAMP_KEY = 'user_permissions_timestamp';

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(() => {
    // Initialize from cache for faster initial load (with TTL check)
    const cached = localStorage.getItem(CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    
    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp, 10);
      if (age < CACHE_TTL) {
        return JSON.parse(cached);
      } else {
      }
    }
    return [];
  });
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(() => {
    return localStorage.getItem('is_platform_admin') === 'true';
  });
  const [loading, setLoading] = useState(true);

  // Check if cache is valid
  const isCacheValid = useCallback(() => {
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    if (!timestamp) return false;
    const age = Date.now() - parseInt(timestamp, 10);
    return age < CACHE_TTL;
  }, []);

  useEffect(() => {
    // Only fetch if cache is invalid or user just logged in
    const token = localStorage.getItem('access_token');
    if (!token) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    // If cache is valid, use it and skip API call
    if (isCacheValid() && permissions.length > 0) {
      setLoading(false);
      return;
    }

    // Otherwise fetch fresh permissions
    fetchPermissions();
  }, []);

  const fetchPermissions = async (force = false) => {
    try {
      // Only fetch if user is logged in (has token)
      const token = localStorage.getItem('access_token');
      if (!token) {
        setPermissions([]);
        clearPermissionCache();
        setLoading(false);
        return;
      }

      // Skip if cache is valid and not forcing refresh
      if (!force && isCacheValid() && permissions.length > 0) {
        setLoading(false);
        return;
      }

      const response = await authApi.getUserPermissions();
      const userPermissions = response.data.permissions || [];
      const isPlatAdmin = Boolean(response.data.is_platform_admin);
      
      setPermissions(userPermissions);
      setIsPlatformAdmin(isPlatAdmin);
      
      // Cache permissions with timestamp
      localStorage.setItem(CACHE_KEY, JSON.stringify(userPermissions));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      localStorage.setItem('is_platform_admin', String(isPlatAdmin));
      
    } catch (error) {
      logger.error('[Permissions] Failed to fetch:', error);
      // Try to load from cache if API fails (even if expired)
      const cachedPermissions = localStorage.getItem(CACHE_KEY);
      if (cachedPermissions) {
        setPermissions(JSON.parse(cachedPermissions));
      } else {
        setPermissions([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const refetchPermissions = useCallback(() => {
    setLoading(true);
    fetchPermissions(true); // Force refresh
  }, []);

  const clearPermissionCache = useCallback(() => {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    localStorage.removeItem('is_platform_admin');
    setPermissions([]);
    setIsPlatformAdmin(false);
  }, []);

  const hasPermission = useCallback((permission) => {
    return permissions.includes(permission);
  }, [permissions]);

  const hasAnyPermission = useCallback((permissionsList) => {
    return permissionsList.some(perm => permissions.includes(perm));
  }, [permissions]);

  return (
    <PermissionsContext.Provider 
      value={{ 
        permissions, 
        hasPermission, 
        hasAnyPermission, 
        isPlatformAdmin,
        loading, 
        refetchPermissions,
        clearPermissionCache
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};
